<script setup>
import { onMounted, onUnmounted, computed, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import LiveVisitorsAPI from 'dashboard/api/liveVisitors';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ChatListHeader from 'dashboard/components/ChatListHeader.vue';

defineProps({
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const visitors = shallowRef([]);
const isLoading = ref(true);
const isInitialLoad = ref(true);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const refreshInterval = ref(null);
const isPageVisible = ref(true);

const accountId = computed(() => store.getters.getCurrentAccountId);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const defaultInbox = computed(() => inboxes.value?.[0]);
const conversationId = computed(() => route.params.conversation_id);

// Get online contact IDs from store (updated by WebSocket presence updates)
const contactsPresence = computed(() => {
  const allContacts = store.getters['contacts/getContacts'];
  const onlineContacts = {};
  Object.values(allContacts || {}).forEach(contact => {
    if (contact.availability_status === 'online') {
      onlineContacts[contact.id] = 'online';
    }
  });
  return onlineContacts;
});

const fetchLiveVisitors = async (showLoading = false) => {
  try {
    // Only show loading on initial load, not during background polling
    if (showLoading || isInitialLoad.value) {
      isLoading.value = true;
    }
    const response = await LiveVisitorsAPI.get(currentPage.value);
    const fetchedVisitors = response.data.payload || [];

    // Merge with current presence from store (WebSocket updates)
    // Using shallowRef so we assign a new array to trigger reactivity
    visitors.value = fetchedVisitors.map(visitor => ({
      ...visitor,
      // Ensure visit_count is a number
      visit_count: visitor.visit_count || 0,
      // Ensure source_display has a default
      source_display: visitor.source_display || 'Direct',
      // Merge online status from presence
      is_online:
        contactsPresence.value[visitor.id.toString()] === 'online' ||
        visitor.is_online,
    }));

    totalPages.value = response.data.meta.total_pages || 1;
    // Use actual online count from presence if available, otherwise use API count
    const onlineCount = Object.keys(contactsPresence.value).length;
    totalCount.value =
      onlineCount > 0 ? onlineCount : response.data.meta.total_count || 0;

    // Update store count for sidebar badge - use the most accurate count
    const accurateCount =
      onlineCount > 0 ? onlineCount : response.data.meta.total_count || 0;
    store.dispatch('contacts/setLiveVisitorsCount', accurateCount);
  } catch (error) {
    // Error fetching live visitors
  } finally {
    isLoading.value = false;
    isInitialLoad.value = false;
  }
};

const startConversation = async visitor => {
  try {
    // First, check if visitor has any existing conversations in any inbox
    // Find the contact_inbox with the most recent conversation (by last message or updated_at)
    const contactInboxes = visitor.contact_inboxes || [];
    const contactInboxesWithConversations = contactInboxes.filter(
      ci => ci.current_conversation?.display_id
    );
    
    // If multiple conversations exist, use the one with the most recent activity
    const contactInboxWithConversation = contactInboxesWithConversations.length > 0
      ? contactInboxesWithConversations.reduce((mostRecent, current) => {
          const currentLastMessage = current.current_conversation?.last_message;
          const mostRecentLastMessage = mostRecent.current_conversation?.last_message;
          
          // Compare by last message timestamp if available
          if (currentLastMessage?.created_at && mostRecentLastMessage?.created_at) {
            return new Date(currentLastMessage.created_at) > new Date(mostRecentLastMessage.created_at)
              ? current
              : mostRecent;
          }
          // If no last message timestamps, use the first one found
          return mostRecent;
        })
      : null;

    // If visitor has an existing conversation, use that inbox
    if (contactInboxWithConversation?.current_conversation?.display_id) {
      const existingConversationId =
        contactInboxWithConversation.current_conversation.display_id;

      // Check if conversation is green (check state) and mark as checked
      const colorState = getVisitorColorState(visitor);
      if (colorState === 'green') {
        // Mark conversation as checked by updating agent_last_seen_at
        ConversationApi.markMessageRead({ id: existingConversationId }).catch(() => {
          // Silently fail if update fails
        });
      }

      // Start fetching conversation data in parallel with navigation
      // This makes the experience feel faster
      store.dispatch('getConversation', existingConversationId);

      router.push({
        name: 'live_visitors_conversation',
        params: {
          accountId: accountId.value,
          conversation_id: existingConversationId,
        },
      });
      return;
    }

    // If no existing conversation, use the default inbox (first inbox)
    // This handles the case where visitor doesn't have any conversations yet
    const targetInboxId = defaultInbox.value?.id;
    if (!targetInboxId) {
      // No inbox available
      return;
    }

    // Use Live Visitors API which finds existing conversations or creates new one
    // This ensures we use the same conversation created by the widget
    const response = await LiveVisitorsAPI.createConversation({
      contact_id: visitor.id,
      inbox_id: targetInboxId,
      message: '', // Optional - can be empty
    });

    const newConversationId = response.data.display_id;

    // Check if conversation is green (check state) and mark as checked
    const colorState = getVisitorColorState(visitor);
    if (colorState === 'green') {
      // Mark conversation as checked by updating agent_last_seen_at
      ConversationApi.markMessageRead({ id: newConversationId }).catch(() => {
        // Silently fail if update fails
      });
    }

    // Start fetching conversation data in parallel with navigation
    store.dispatch('getConversation', newConversationId);

    // Navigate to conversation view using display_id
    router.push({
      name: 'live_visitors_conversation',
      params: {
        accountId: accountId.value,
        conversation_id: newConversationId,
      },
    });
  } catch (error) {
    // Error creating conversation
  }
};

// Start polling when page is visible
const startPolling = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
  refreshInterval.value = setInterval(() => {
    if (isPageVisible.value) {
      fetchLiveVisitors();
    }
  }, 6000); // 6 seconds - optimized for live data with good performance
};

// Stop polling to save resources
const stopPolling = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
    refreshInterval.value = null;
  }
};

// Watch for WebSocket presence updates (instant updates every ~20 seconds)
watch(
  contactsPresence,
  newPresence => {
    // Update total count based on online contacts from WebSocket
    const onlineCount = Object.keys(newPresence).length;
    if (onlineCount > 0) {
      totalCount.value = onlineCount;
      // Update store count for sidebar badge immediately
      store.dispatch('contacts/setLiveVisitorsCount', onlineCount);
    }

    if (visitors.value.length > 0) {
      // Update visitor online status from store presence (WebSocket updates)
      visitors.value = visitors.value.map(visitor => {
        const isOnline = newPresence[visitor.id.toString()] === 'online';
        return {
          ...visitor,
          is_online: isOnline || visitor.is_online,
        };
      });
    }
  },
  { deep: true }
);

// Page Visibility API handler - pause polling when tab is hidden for better performance
let handleVisibilityChange = null;

onMounted(() => {
  // Initial fetch with loading indicator
  fetchLiveVisitors(true);

  // Page Visibility API - pause polling when tab is hidden for better performance
  handleVisibilityChange = () => {
    isPageVisible.value = !document.hidden;
    if (isPageVisible.value) {
      // Page became visible - fetch quietly (no loading indicator) and resume polling
      fetchLiveVisitors(false);
      startPolling();
    } else {
      // Page hidden - pause polling to save resources
      stopPolling();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Start polling initially (page is visible)
  startPolling();
});

onUnmounted(() => {
  if (handleVisibilityChange) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  stopPolling();
});

// Watch for conversation changes to emit load event and mark green conversations as checked
watch(conversationId, async (newConversationId) => {
  if (newConversationId) {
    emit('conversationLoad');
    
    // Find the visitor with this conversation and check if it's green
    const visitor = visitors.value.find(v => {
      return v.contact_inboxes?.some(ci => 
        ci.current_conversation?.display_id === Number(newConversationId)
      );
    });
    
    if (visitor) {
      const colorState = getVisitorColorState(visitor);
      if (colorState === 'green') {
        // Mark conversation as checked by updating agent_last_seen_at
        ConversationApi.markMessageRead({ id: newConversationId }).catch(() => {
          // Silently fail if update fails
        });
        
        // Refresh live visitors to update the label
        fetchLiveVisitors();
      }
    }
  }
});

// Helper function to get last message from visitor
const getLastMessage = visitor => {
  // Check all contact_inboxes and find the one with the most recent conversation
  const contactInboxes = visitor.contact_inboxes || [];
  const inboxWithMessage = contactInboxes.find(contactInbox => {
    const lastMessage = contactInbox?.current_conversation?.last_message;
    return lastMessage?.content;
  });
  return inboxWithMessage?.current_conversation?.last_message?.content || null;
};

// Helper function to get unread count from visitor
const getUnreadCount = visitor => {
  // Sum unread counts from all contact_inboxes
  const contactInboxes = visitor.contact_inboxes || [];
  return contactInboxes.reduce((total, contactInbox) => {
    const unreadCount = contactInbox?.current_conversation?.unread_count || 0;
    return total + unreadCount;
  }, 0);
};

// Helper function to check if last message is incoming (from visitor)
const isLastMessageIncoming = visitor => {
  const contactInboxes = visitor.contact_inboxes || [];
  const inboxWithMessage = contactInboxes.find(contactInbox => {
    return contactInbox?.current_conversation?.last_message;
  });
  if (inboxWithMessage) {
    const lastMessage = inboxWithMessage.current_conversation.last_message;
    // Handle both string ('incoming') and integer (0) values
    const messageType = lastMessage.message_type;
    return messageType === 'incoming' || messageType === 0;
  }
  return false;
};

// Helper function to truncate message
const truncateMessage = message => {
  if (!message) return '';
  const maxLength = 50;
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + '...';
};

// Helper function to get conversation state from visitor
const getConversationState = visitor => {
  const contactInboxes = visitor.contact_inboxes || [];
  // Find the contact_inbox with the most recent conversation
  const contactInboxWithConversation = contactInboxes.find(
    ci => ci.current_conversation?.display_id
  );
  
  if (!contactInboxWithConversation?.current_conversation) {
    return null;
  }
  
  return {
    is_first_message_bot: contactInboxWithConversation.current_conversation.is_first_message_bot || false,
    has_user_replied: contactInboxWithConversation.current_conversation.has_user_replied || false,
    has_agent_replied: contactInboxWithConversation.current_conversation.has_agent_replied || false,
    last_message_created_at: contactInboxWithConversation.current_conversation.last_message_created_at,
    last_message: contactInboxWithConversation.current_conversation.last_message,
    agent_last_seen_at: contactInboxWithConversation.current_conversation.agent_last_seen_at,
  };
};

// Helper function to check if new message after 30 minutes
const hasNewMessageAfter30Min = (conversationState, agentLastSeen) => {
  if (!conversationState?.last_message_created_at || !agentLastSeen) {
    return false;
  }
  
  const lastMessageTime = new Date(conversationState.last_message_created_at).getTime();
  const agentLastSeenTime = typeof agentLastSeen === 'string' 
    ? new Date(agentLastSeen).getTime()
    : agentLastSeen > 1000000000000 ? agentLastSeen : agentLastSeen * 1000;
  
  const thirtyMinutesInMs = 30 * 60 * 1000; // 30 minutes
  const timeSinceLastSeen = lastMessageTime - agentLastSeenTime;
  
  // Check if last message is incoming and was created after agent last seen + 30 min
  const isLastMessageIncoming = conversationState.last_message?.message_type === 'incoming' || 
                                  conversationState.last_message?.message_type === 0;
  
  return isLastMessageIncoming && timeSinceLastSeen > thirtyMinutesInMs;
};

// Helper function to get visitor color state: 'green', 'yellow', or 'white'
const getVisitorColorState = visitor => {
  const conversationState = getConversationState(visitor);
  if (!conversationState) {
    return 'white'; // No conversation, default to white
  }
  
  // GREEN: Bot sent first message and user hasn't replied
  if (conversationState.is_first_message_bot && !conversationState.has_user_replied) {
    return 'green';
  }
  
  // YELLOW: User has replied (unread/unchecked) OR new message after 30 minutes
  const unreadCount = getUnreadCount(visitor);
  if (unreadCount > 0) {
    return 'yellow';
  }
  
  // Check if there's a new message after 30 minutes since agent last seen
  if (conversationState.agent_last_seen_at && conversationState.last_message_created_at) {
    const lastMessageTime = new Date(conversationState.last_message_created_at).getTime();
    const agentLastSeenTime = typeof conversationState.agent_last_seen_at === 'string' 
      ? new Date(conversationState.agent_last_seen_at).getTime()
      : conversationState.agent_last_seen_at > 1000000000000 
        ? conversationState.agent_last_seen_at 
        : conversationState.agent_last_seen_at * 1000;
    
    const thirtyMinutesInMs = 30 * 60 * 1000; // 30 minutes
    const timeSinceLastSeen = lastMessageTime - agentLastSeenTime;
    
    // Check if last message is incoming and was created more than 30 minutes after agent last seen
    const isLastMessageIncoming = conversationState.last_message?.message_type === 'incoming' || 
                                    conversationState.last_message?.message_type === 0;
    
    if (isLastMessageIncoming && timeSinceLastSeen > thirtyMinutesInMs) {
      return 'yellow';
    }
  }
  
  // If user has replied but agent hasn't, show as yellow
  if (conversationState.has_user_replied && !conversationState.has_agent_replied) {
    return 'yellow';
  }
  
  // Check 3 hours inactivity (existing logic)
  const lastActivityAt = visitor.last_activity_at || visitor.presence?.last_seen_at;
  if (lastActivityAt) {
    let lastActivityTimestamp;
    if (typeof lastActivityAt === 'string') {
      lastActivityTimestamp = new Date(lastActivityAt).getTime();
    } else if (lastActivityAt > 1000000000000) {
      lastActivityTimestamp = lastActivityAt;
    } else {
      lastActivityTimestamp = lastActivityAt * 1000;
    }
    
    const now = Date.now();
    const threeHoursInMs = 3 * 60 * 60 * 1000;
    const timeSinceLastActivity = now - lastActivityTimestamp;
    
    if (timeSinceLastActivity > threeHoursInMs) {
      return 'yellow';
    }
  }
  
  // WHITE: Agent has replied (checked)
  if (conversationState.has_agent_replied) {
    return 'white';
  }
  
  // Default to white
  return 'white';
};

// Helper function to get background color for visitor
const getVisitorBackgroundColor = visitor => {
  // Don't apply background color if this is the active conversation
  const isActiveConversation = conversationId.value && 
    visitor.contact_inboxes?.some(ci => ci.current_conversation?.display_id === Number(conversationId.value));
  
  if (isActiveConversation) {
    return {};
  }
  
  const colorState = getVisitorColorState(visitor);
  const conversationState = getConversationState(visitor);
  
  // If green conversation has been checked (opened), remove green background
  if (colorState === 'green' && conversationState?.agent_last_seen_at) {
    const agentLastSeenTime = typeof conversationState.agent_last_seen_at === 'string' 
      ? new Date(conversationState.agent_last_seen_at).getTime()
      : conversationState.agent_last_seen_at > 1000000000000 
        ? conversationState.agent_last_seen_at 
        : conversationState.agent_last_seen_at * 1000;
    
    const now = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes threshold
    
    // If agent_last_seen_at is within last 5 minutes, remove green background (checked)
    if (now - agentLastSeenTime < fiveMinutesInMs) {
      return {}; // No background color for checked green conversations
    }
  }
  
  if (colorState === 'green') {
    return { backgroundColor: '#86efac' }; // Light green
  } else if (colorState === 'yellow') {
    return { backgroundColor: '#fef08a' }; // Light yellow
  }
  
  // White (default) - no background color
  return {};
};

// Helper function to check if visitor should be marked as uncheck (more than 3 hours since last activity)
const shouldShowAsUncheck = visitor => {
  const colorState = getVisitorColorState(visitor);
  // Yellow means uncheck, green and white mean checked
  return colorState === 'yellow';
};

// Helper function to get the check label text
const getCheckLabel = visitor => {
  const colorState = getVisitorColorState(visitor);
  const conversationState = getConversationState(visitor);
  
  // If green conversation has been opened (agent_last_seen_at is recent), show "checked"
  if (colorState === 'green' && conversationState?.agent_last_seen_at) {
    const agentLastSeenTime = typeof conversationState.agent_last_seen_at === 'string' 
      ? new Date(conversationState.agent_last_seen_at).getTime()
      : conversationState.agent_last_seen_at > 1000000000000 
        ? conversationState.agent_last_seen_at 
        : conversationState.agent_last_seen_at * 1000;
    
    const now = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes threshold
    
    // If agent_last_seen_at is within last 5 minutes, consider it checked
    if (now - agentLastSeenTime < fiveMinutesInMs) {
      return t('LIVE_VISITORS.CHECKED');
    }
  }
  
  // Green and yellow show "check", white shows "checked"
  return colorState === 'white' ? t('LIVE_VISITORS.CHECKED') : t('LIVE_VISITORS.CHECK');
};
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <ChatListHeader
      :page-title="t('LIVE_VISITORS.TITLE')"
      :has-applied-filters="false"
      :has-active-folders="false"
      active-status="open"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="{
        mineCount: 0,
        unassignedCount: totalCount,
        allCount: totalCount,
        assignedCount: 0,
      }"
      :is-list-loading="isLoading && !visitors.length"
    />

    <div
      v-if="isLoading && !visitors.length"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="visitors.length === 0"
      class="flex flex-col items-center justify-center py-20 px-4 flex-1"
    >
      <div class="text-slate-400 text-lg mb-2 font-medium">
        {{ t('LIVE_VISITORS.EMPTY_STATE.TITLE') }}
      </div>
      <div class="text-slate-500 text-sm text-center max-w-md">
        {{ t('LIVE_VISITORS.EMPTY_STATE.SUBTITLE') }}
      </div>
    </div>

    <div v-else class="flex-1 overflow-y-auto">
      <div class="divide-y divide-n-weak">
        <div
          v-for="visitor in visitors"
          :key="visitor.id"
          class="flex items-center justify-between px-4 py-3 hover:bg-n-strong cursor-pointer transition-colors"
          :class="{
            'bg-n-base-50':
              conversationId &&
              visitor.contact_inboxes?.some(
                ci =>
                  ci.current_conversation?.display_id === Number(conversationId)
              ),
          }"
          :style="getVisitorBackgroundColor(visitor)"
          @click="startConversation(visitor)"
        >
          <div class="flex items-center space-x-3 flex-1 min-w-0">
            <!-- Avatar with online indicator -->
            <div class="relative flex-shrink-0">
              <img
                v-if="visitor.avatar_url"
                :src="visitor.avatar_url"
                :alt="visitor.name"
                class="w-10 h-10 rounded-full object-cover"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full bg-n-base-300 flex items-center justify-center text-n-slate-11 font-semibold text-sm"
              >
                {{ visitor.name?.charAt(0)?.toUpperCase() || '?' }}
              </div>
              <span
                class="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-n-solid-1 rounded-full"
              />
            </div>

            <!-- Visitor Info -->
            <div class="flex-1 min-w-0">
              <!-- Expanded layout: 4 equal sections with reduced spacing and right padding -->
              <template v-if="isOnExpandedLayout">
                <div class="grid grid-cols-4 gap-2 pr-6 text-xs">
                  <!-- Section 1: Name, Last Message, Inbox, and Unread Count -->
                  <div class="flex flex-col min-w-0">
                    <div class="flex items-center gap-2 mb-0.5">
                      <div
                        class="text-sm text-n-slate-12 truncate"
                        :class="
                          getUnreadCount(visitor) > 0
                            ? 'font-semibold'
                            : 'font-medium'
                        "
                      >
                        {{ visitor.name || t('LIVE_VISITORS.ANONYMOUS') }}
                      </div>
                      <!-- Unread count badge - teal green like conversations page -->
                      <span
                        v-if="getUnreadCount(visitor) > 0"
                        class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9 flex-shrink-0"
                      >
                        {{
                          getUnreadCount(visitor) > 9
                            ? t('LIVE_VISITORS.UNREAD_COUNT_OVERFLOW')
                            : getUnreadCount(visitor)
                        }}
                      </span>
                      <!-- Check/Checked label -->
                      <span
                        class="text-xs flex-shrink-0"
                        :class="
                          getVisitorColorState(visitor) === 'white'
                            ? 'text-n-slate-10'
                            : 'text-n-slate-11'
                        "
                      >
                        {{ getCheckLabel(visitor) }}
                      </span>
                    </div>
                    <div
                      v-if="getLastMessage(visitor)"
                      class="truncate mb-0.5 text-sm leading-6 h-6"
                      :class="
                        getUnreadCount(visitor) > 0
                          ? 'text-n-slate-12 font-semibold'
                          : 'text-n-slate-11'
                      "
                    >
                      <span
                        v-if="!isLastMessageIncoming(visitor)"
                        class="text-n-slate-10"
                        >{{ t('LIVE_VISITORS.OUTGOING_MESSAGE_ICON') }}
                      </span>
                      {{ truncateMessage(getLastMessage(visitor)) }}
                    </div>
                    <p
                      v-else
                      class="text-n-slate-11 text-sm mb-0.5 leading-6 h-6 truncate"
                    >
                      {{ t('CHAT_LIST.NO_MESSAGES') }}
                    </p>
                    <div
                      v-if="visitor.contact_inboxes?.length"
                      class="text-n-slate-10 truncate text-xs"
                    >
                      {{ visitor.contact_inboxes[0].inbox_name }}
                    </div>
                  </div>

                  <!-- Section 2: Visit Count -->
                  <div class="flex items-center text-n-slate-11">
                    {{ visitor.visit_count || 0 }}
                    {{
                      (visitor.visit_count || 0) === 1
                        ? t('LIVE_VISITORS.VISIT')
                        : t('LIVE_VISITORS.VISITS')
                    }}
                  </div>

                  <!-- Section 3: Source -->
                  <div
                    class="flex items-center text-n-slate-11 truncate"
                    :title="visitor.source_display"
                  >
                    {{ visitor.source_display || 'Direct' }}
                  </div>

                  <!-- Section 4: Current Page Title -->
                  <div
                    class="flex items-center text-n-slate-11 truncate"
                    :title="visitor.current_page_title"
                  >
                    {{ visitor.current_page_title || '-' }}
                  </div>
                </div>
              </template>

              <!-- Collapsed layout: Name, last message, inbox, and visit count -->
              <template v-else>
                <div class="flex flex-col min-w-0 flex-1">
                  <div class="flex items-center justify-between mb-0.5">
                    <h3
                      class="text-sm text-n-slate-12 truncate"
                      :class="
                        getUnreadCount(visitor) > 0
                          ? 'font-semibold'
                          : 'font-medium'
                      "
                    >
                      {{ visitor.name || t('LIVE_VISITORS.ANONYMOUS') }}
                    </h3>
                    <div class="flex items-center gap-2 ml-2 flex-shrink-0">
                      <!-- Unread count badge - teal green like conversations page -->
                      <span
                        v-if="getUnreadCount(visitor) > 0"
                        class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9"
                      >
                        {{
                          getUnreadCount(visitor) > 9
                            ? t('LIVE_VISITORS.UNREAD_COUNT_OVERFLOW')
                            : getUnreadCount(visitor)
                        }}
                      </span>
                      <!-- Check/Checked label -->
                      <span
                        class="text-xs flex-shrink-0"
                        :class="
                          getVisitorColorState(visitor) === 'white'
                            ? 'text-n-slate-10'
                            : 'text-n-slate-11'
                        "
                      >
                        {{ getCheckLabel(visitor) }}
                      </span>
                      <span
                        v-if="visitor.visit_count"
                        class="text-xs text-n-slate-11"
                      >
                        {{ visitor.visit_count }}
                        {{
                          visitor.visit_count === 1
                            ? t('LIVE_VISITORS.VISIT')
                            : t('LIVE_VISITORS.VISITS')
                        }}
                      </span>
                      <span
                        v-if="visitor.contact_inboxes?.length"
                        class="text-xs text-n-slate-10"
                      >
                        {{ visitor.contact_inboxes[0].inbox_name }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="getLastMessage(visitor)"
                    class="text-sm truncate leading-6 h-6"
                    :class="
                      getUnreadCount(visitor) > 0
                        ? 'text-n-slate-12 font-semibold'
                        : 'text-n-slate-11'
                    "
                  >
                    <span
                      v-if="!isLastMessageIncoming(visitor)"
                      class="text-n-slate-10"
                      >{{ t('LIVE_VISITORS.OUTGOING_MESSAGE_ICON') }}
                    </span>
                    {{ truncateMessage(getLastMessage(visitor)) }}
                  </div>
                  <p
                    v-else
                    class="text-n-slate-11 text-sm leading-6 h-6 truncate"
                  >
                    {{ t('CHAT_LIST.NO_MESSAGES') }}
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
